import React, { useState, useEffect } from 'react';
//Context
import { LlamadasProvider, useLlamadas } from './LlamadasContext';
//Components
import NuevoTipoLlamada from './NuevoTipoLlamada';
import EditarTipoLlamada from './EditarTipoLlamada';
//Material-UI
import { DataGrid, esES } from '@material-ui/data-grid';
import { Chip, Snackbar, TextField, IconButton } from '@material-ui/core';
//Icons
import { Check } from 'tabler-icons-react';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
//Styles
import '../../styles/llamadas.css';
import '../../App.css';

function escapeRegExp(value) {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function Llamadas() {
    const {
        getInfo,
        llamadas,
        GreenChip,
        YellowChip,
        RedChip,
        BlueChip,
        snackbar,
        setSnackbar,
        snackbarText,
        setIsEdit,
        getTheCallType,
    } = useLlamadas();
    const [selectionModel, setSelectionModel] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [rows, setRows] = useState([]);

    useEffect(() => {
        setRows(llamadas);
    }, [llamadas]);

    const requestSearch = (value) => {
        setSearchText(value);
        if (!value) {
            setRows(llamadas);
            return;
        }
        const re = new RegExp(escapeRegExp(value), 'i');
        setRows(
            llamadas.filter((row) =>
                Object.keys(row).some((f) => re.test(row[f]?.toString() ?? ''))
            )
        );
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(false);
    };

    function renderColor(params) {
        switch (params.value) {
            case 'Verde':
                return <GreenChip label="Verde" />;
            case 'Amarillo':
                return <YellowChip label="Amarillo" />;
            case 'Rojo':
                return <RedChip label="Rojo" />;
            case 'Azul':
                return <BlueChip label="Azul" />;
            default:
                return <Chip label="Sin color" className="chip-sin-color" />;
        }
    }

    const llamadasColumns = [
        {
            field: 'tipoLlamada',
            headerName: 'Código',
            flex: 0.7,
            minWidth: 80,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'colorName',
            headerName: 'Color',
            renderCell: renderColor,
            flex: 0.7,
            minWidth: 110,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'areaAtencionName',
            headerName: 'Área de atención',
            flex: 1.1,
            minWidth: 130,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'nombre',
            headerName: 'Nombre',
            flex: 1.8,
            minWidth: 140,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'esDowntime',
            headerName: 'Es Paro',
            type: 'boolean',
            flex: 0.8,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'esSonora',
            headerName: 'Sonora',
            type: 'boolean',
            flex: 0.8,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'requiereClasificacion',
            headerName: 'Clasificar',
            type: 'boolean',
            flex: 0.8,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
        },
    ];

    useEffect(() => {
        getInfo('tiposllamada/Listado');
    }, [getInfo]);

    useEffect(() => {
        setSelectionModel([]);
    }, [llamadas]);

    return (
        <div className="llamadas-page">
            <Snackbar
                open={snackbar}
                autoHideDuration={3000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <div className="app-notif app-notif--success">
                    <Check size={20} strokeWidth={2} color="#fff" />
                    <span className="app-notif__msg">{snackbarText}</span>
                    <div className="app-notif__progress" />
                </div>
            </Snackbar>

            <div className="llamadas-header">
                <div className="llamadas-header-left">
                    <h1 className="titulo">Tipos de llamadas</h1>
                    <p className="desc">
                        Agregar y editar tipos de llamadas, así como la información relacionada a
                        ellos.
                    </p>
                </div>
            </div>

            <div className="llamadas-body">
                <div className="llamadas-card">
                    <div className="llamadas-card-toolbar">
                        <span className="llamadas-selected-count">
                            {selectionModel.length > 0
                                ? `${selectionModel.length} seleccionado${selectionModel.length > 1 ? 's' : ''}`
                                : ''}
                        </span>

                        <TextField
                            variant="outlined"
                            size="small"
                            value={searchText}
                            onChange={(e) => requestSearch(e.target.value)}
                            placeholder="Buscar..."
                            className="table-search-inline"
                            InputProps={{
                                startAdornment: <SearchIcon fontSize="small" />,
                                endAdornment: (
                                    <IconButton
                                        size="small"
                                        className={
                                            searchText
                                                ? 'table-search-clear-visible'
                                                : 'table-search-clear-hidden'
                                        }
                                        onClick={() => requestSearch('')}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                        />

                        <div className="llamadas-card-actions">
                            <EditarTipoLlamada />
                            <NuevoTipoLlamada />
                        </div>
                    </div>
                    <div className="llamadas-table-wrapper">
                        <DataGrid
                            columns={llamadasColumns}
                            rows={rows}
                            localeText={esES.props.MuiDataGrid.localeText}
                            onSelectionModelChange={(newSelectionModel) => {
                                if (newSelectionModel.length > 0) {
                                    setIsEdit(newSelectionModel.length === 1);
                                    getTheCallType(newSelectionModel[0]);
                                } else {
                                    setIsEdit(false);
                                }
                                setSelectionModel(newSelectionModel);
                            }}
                            selectionModel={selectionModel}
                            hideFooterSelectedRowCount={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

const LlamadasPage = () => (
    <LlamadasProvider>
        <Llamadas />
    </LlamadasProvider>
);

export default LlamadasPage;
